import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Attribute, useTimeSettings, WorkflowCondition } from "lib/api";
import LoadingContainer from "components/layout/LoadingContainer";

import { QueryBuilderLabels } from "./types";
import { useWorflowOperators } from "./config";

import QueryBuilder, { QueryBuilderProps } from "./QueryBuilder";
import {
  locationAttribute,
  queryToWorkflowCondition,
} from "./queryToWorkflowConditions";
import { typeNormalizer } from "./utils";
import { workflowConditionsToQuery } from "./workflowConditionsToQuery";

export interface WorkflowQueryBuilderProps
  extends Pick<QueryBuilderProps, "onChange" | "marker"> {
  value?: WorkflowCondition[];
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  attributes?: Record<string, Attribute>;
  loading?: boolean;
}

const WorkflowQueryBuilder = ({
  value,
  setFieldValue,
  attributes: attributesProp,
  loading = false,
  marker,
}: WorkflowQueryBuilderProps): JSX.Element => {
  const { t } = useTranslation("queryBuilder");

  const { combinators, operators } = useWorflowOperators(t);
  const labels = useMemo(
    () =>
      t("component", {
        returnObjects: true,
      }) as QueryBuilderLabels,
    [t]
  );

  const attributes = useMemo((): Record<string, Attribute> => {
    const toReturn: typeof attributesProp = {
      location: locationAttribute,
    };

    if (attributesProp != null) {
      Object.keys(attributesProp).forEach((key) => {
        const att = attributesProp[key];
        if (att.type === "hyperlink" || att.type === "image") return;
        toReturn[key] = {
          ...att,
          type: typeNormalizer(att.type),
        };
      });
    }

    return toReturn;
  }, [attributesProp]);

  const { timezone, fromServer } = useTimeSettings(false);

  // this is an uncontrolable component, the value property only initializes it
  const [initialAndFinalValue] = useState(value);
  // it is only worth to build the query when we have the real domain timezone, not the default one
  const initialQuery = useMemo(
    () =>
      fromServer
        ? workflowConditionsToQuery(initialAndFinalValue, timezone)
        : undefined,
    [initialAndFinalValue, fromServer, timezone]
  );

  const handleChange = useCallback(
    (query) => {
      const conditions = queryToWorkflowCondition(
        query,
        attributesProp,
        timezone
      );

      setFieldValue("conditions", conditions, false);
    },
    [attributesProp, setFieldValue, timezone]
  );

  return (
    <LoadingContainer hidden={!loading}>
      <QueryBuilder
        attributes={attributes}
        query={initialQuery}
        onChange={handleChange}
        combinators={combinators}
        operators={operators}
        labels={labels}
        marker={marker}
      />
    </LoadingContainer>
  );
};

export default WorkflowQueryBuilder;
